import moment from "moment";
import { mapFeedbackRow } from "modules/feedback/action";
import { ORDER_STATUS } from "./orderStatus";
import {
  DISCOUNT_STATUS,
  ROLE_TO_USER_TYPE,
  USER_ROLE,
} from "./userType";
import {
  displayOrNA,
  isLocalPreviewUrl,
  parseSelectedAddressType,
} from "./commonFunctions";
import {
  SERVICE_CATEGORY_LABEL,
  toServiceCategory,
} from "./serviceCategory";
import {
  ALL_COUPON_CATEGORIES,
  COUPON_CATEGORY_OPTIONS,
  COUPON_TYPE,
  getCouponStatusLabel,
  getCouponTypeLabel,
  INITIAL_COUPON_VALUES,
} from "./constants";

export type PaginatedResult = {
  result: any[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalRecords: number;
  };
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  nextHit: boolean;
};

const toPositiveNumber = (value: any, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const splitFullName = (fullName: string = "") => {
  const parts = `${fullName}`.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
};

/** `<input type="date">` only accepts the YYYY-MM-DD form. */
export const toDateInputValue = (date: any) =>
  date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : "";

const SCHEDULE_CLOCK_FORMATS = ["hh:mm A", "h:mm A", "HH:mm", "H:mm"] as const;

/**
 * API `scheduleTime` may be an ISO datetime ("2027-05-04T09:30:00.000Z")
 * or a wall-clock string ("04:54 PM"). Returns "HH:mm" for TimePicker.
 */
export const parseScheduleTimeToHHmm = (scheduleTime: any): string => {
  const raw = `${scheduleTime || ""}`.trim();
  if (!raw) return "";

  const clock = moment(raw, SCHEDULE_CLOCK_FORMATS as unknown as string[], true);
  if (clock.isValid()) return clock.format("HH:mm");

  // Legacy ISO / datetime payloads
  if (raw.includes("T") || raw.endsWith("Z")) {
    const iso = moment(raw);
    if (iso.isValid()) return iso.format("HH:mm");
  }

  return "";
};

/**
 * Combines scheduleDate + scheduleTime into a moment for the next-day edit gate.
 * Handles ISO scheduleTime or clock strings like "04:54 PM".
 */
export const parseScheduleDateTime = (
  scheduleDate: any,
  scheduleTime: any
): moment.Moment | null => {
  const dateRaw = `${scheduleDate || ""}`.trim();
  const timeRaw = `${scheduleTime || ""}`.trim();

  if (timeRaw) {
    if (timeRaw.includes("T") || timeRaw.endsWith("Z")) {
      const iso = moment(timeRaw);
      if (iso.isValid()) return iso;
    }

    const clock = moment(
      timeRaw,
      SCHEDULE_CLOCK_FORMATS as unknown as string[],
      true
    );
    if (clock.isValid()) {
      if (dateRaw) {
        const datePart = moment(dateRaw);
        if (datePart.isValid()) {
          return datePart
            .clone()
            .hour(clock.hour())
            .minute(clock.minute())
            .second(0)
            .millisecond(0);
        }
      }
      return clock;
    }
  }

  if (dateRaw) {
    const dateOnly = moment(dateRaw);
    return dateOnly.isValid() ? dateOnly : null;
  }

  return null;
};

const toStoredFileUrl = (url: string) => {
  const value = `${url || ""}`.trim();
  if (!value || isLocalPreviewUrl(value) || value.startsWith("data:"))
    return "";
  try {
    const parsed = new URL(value);
    return parsed.search ? `${parsed.origin}${parsed.pathname}` : value;
  } catch {
    return value;
  }
};

const LIST_KEYS = [
  "data",
  "result",
  "vehicles",
  "vehicle",
  "list",
  "docs",
  "items",
  "rows",
];

const extractPaginatedRows = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];

  for (const key of LIST_KEYS) {
    if (Array.isArray(data[key])) return data[key];
  }

  // GET_API_CALL spreads array payloads into `{ 0: item, 1: item, message }`
  const numericKeys = Object.keys(data).filter((key) => /^\d+$/.test(key));
  if (numericKeys.length) {
    return numericKeys
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => data[key]);
  }

  if (data.data && typeof data.data === "object") {
    return extractPaginatedRows(data.data);
  }
  return [];
};

const getPaginationMeta = (data: any) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {};
  if (Array.isArray(data.data)) return data;
  if (data.data && typeof data.data === "object") {
    return { ...data, ...data.data };
  }
  return data;
};

export const mapPaginatedResult = (
  data: any,
  mapItem: (item: any) => any = (item) => item,
): PaginatedResult => {
  const rows = extractPaginatedRows(data);
  const meta = getPaginationMeta(data);
  const page = toPositiveNumber(meta?.page, 1);
  const limit = toPositiveNumber(meta?.limit, rows.length || 10);
  const total = toPositiveNumber(meta?.total, rows.length);
  const totalPages = toPositiveNumber(meta?.totalPages, 1);

  return {
    result: rows.map(mapItem),
    pagination: { page, limit, totalPages, totalRecords: total },
    page,
    limit,
    total,
    totalPages,
    nextHit: Boolean(meta?.nextHit),
  };
};

export const mapUserListItem = (user: any) => {
  const { firstName, lastName } = splitFullName(user?.fullName);
  const id = user?._id || user?.id || "";
  return {
    id,
    _id: id,
    fullName: user?.fullName || "",
    firstName,
    lastName,
    email: user?.email || "",
    phone: user?.phone || "",
    phoneNumber: user?.phone || "",
    city: user?.city || "",
    createdAt: user?.createdAt || "",
    profilePhoto: user?.profilePhoto || "",
  };
};

export const mapOrderInformation = (info: any) => {
  if (!info || typeof info !== "object") return null;
  return {
    ...info,
    totalOrders: info.totalOrders ?? 0,
    totalRevenue: info.totalRevenue ?? 0,
    ordersCancelledByCustomer: info.ordersCancelledByCustomer ?? 0,
    ordersCancelledByFranchise: info.ordersCancelledByFranchise ?? 0,
    totalComplaints: info.totalComplaints ?? 0,
    unresolvedComplaints: info.unresolvedComplaints ?? 0,
    ordersRejectedByFranchise: info.ordersRejectedByFranchise ?? 0,
  };
};

export const mapServicePartnerListItem = (partner: any) => {
  const { firstName, lastName } = splitFullName(partner?.username);
  return {
    id: partner?._id || "",
    _id: partner?._id || "",
    fullName: partner?.username || "",
    firstName,
    lastName,
    email: partner?.email || "",
    phoneNumber: partner?.phoneNumber || "",
    salary: partner?.salary ?? 0,
    dateOfJoining: partner?.dateOfJoining || "",
  };
};

/**
 * Flattens /api/v1/admin/user-details/{id} into the shape the detail and edit
 * screens already render.
 */
export const mapUserDetail = (data: any) => {
  const info = data?.personalInformation || {};
  const address = info?.address || {};
  const { firstName, lastName } = splitFullName(info?.fullName);
  const role = info?.role || "";
  const profilePicture = info?.profilePicture || info?.profilePhoto || "";
  const merchantSource =
    info?.merchant || info?.franchise || data?.merchant || data?.franchise || {};
  const merchantId =
    (typeof merchantSource === "string" ? merchantSource : "") ||
    merchantSource?._id ||
    merchantSource?.id ||
    info?.merchantId ||
    info?.vendorId ||
    data?.merchantId ||
    "";
  const merchantName =
    (typeof merchantSource === "object" &&
      (merchantSource?.fullName ||
        merchantSource?.name ||
        merchantSource?.merchantName)) ||
    info?.merchantName ||
    info?.franchiseName ||
    data?.merchantName ||
    "";
  const { firstName: merchantFirstName, lastName: merchantLastName } =
    splitFullName(merchantName);
  return {
    id: data?._id || "",
    _id: data?._id || "",
    role,
    userType: ROLE_TO_USER_TYPE[role],
    fullName: info?.fullName || "",
    firstName,
    lastName,
    profilePhoto: profilePicture,
    email: info?.email || "",
    phone: info?.phoneNumber || "",
    phoneNumber: info?.phoneNumber || "",
    addressLine1: address?.addressLine1 || address?.line1 || "",
    addressLine2: address?.addressLine2 || address?.line2 || "",
    city: address?.city || "",
    state: address?.state || "",
    country: address?.country || "",
    pincode: address?.pincode || "",
    addressType:
      address?.addressType !== undefined && address?.addressType !== null
        ? `${address.addressType}`
        : info?.addressType !== undefined && info?.addressType !== null
          ? `${info.addressType}`
          : "",
    coordinates: Array.isArray(address?.location?.coordinates)
      ? address.location.coordinates
      : [],
    dateOfJoining: info?.dateOfJoining || "",
    gstNumber: info?.gstNumber || "",
    registrationNumber: info?.registrationNumber || "",
    franchiseAmount: info?.franchiseAmount ?? "",
    numberOfStaff: info?.numberOfServicePartners ?? 0,
    salary: info?.salary ?? 0,
    document: info?.document || info?.documentUrl || data?.document || "",
    vendorId: merchantId,
    merchantId,
    merchant: merchantId
      ? {
          id: merchantId,
          _id: merchantId,
          fullName: merchantName,
          firstName: merchantFirstName,
          lastName: merchantLastName,
          phoneNumber:
            (typeof merchantSource === "object" &&
              (merchantSource?.phoneNumber || merchantSource?.phone)) ||
            "",
        }
      : null,
    orderInformation: mapOrderInformation(data?.orderInformation),
    servicePartnerListing: Array.isArray(data?.servicePartnerListing)
      ? data.servicePartnerListing.map(mapServicePartnerListItem)
      : [],
    vehicle: Array.isArray(data?.vehicle)
      ? data.vehicle.map((v: any) => ({
          _id: v?._id || v?.id || "",
          name: v?.name || "",
          companyName: v?.companyName || "",
        }))
      : [],
  };
};

export const mapCarCompanyListItem = (company: any) => ({
  id: company?._id || company?.id || "",
  _id: company?._id || company?.id || "",
  name: company?.name || "",
  logo: company?.logo || "",
  createdAt: company?.createdAt || "",
});

export const mapCarTypeListItem = (carType: any) => {
  const id = carType?._id || carType?.id || "";
  return {
    id,
    _id: id,
    carType: carType?.carType || carType?.name || carType?.type || "",
    createdAt: carType?.createdAt || "",
  };
};

export const mapServiceTypeItem = (item: any) => {
  const id = item?._id ?? "";
  const serviceName = displayOrNA(item?.serviceName);
  return {
    id,
    _id: id,
    serviceName,
    title: serviceName,
    serviceType: serviceName,
    typeId: item?.typeId ?? "",
    createdAt: item?.createdAt ?? "",
  };
};

/** Service row `_id` from GET /service-price (always present). */
export const getServicePricingRecordId = (item: any): string => {
  const raw = item?._id ?? item?.id;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    return String(raw._id || raw.$oid || raw.id || "");
  }
  return String(raw);
};

/** Price record id sent as `pricings[].id` on update — absent when no price yet. */
export const getServicePriceUpdateId = (item: any): string => {
  const raw = item?.servicePriceId;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    return String(raw._id || raw.$oid || raw.id || "");
  }
  return String(raw);
};

export const mapServicePricingItem = (item: any) => {
  const id = getServicePricingRecordId(item);
  const servicePriceId = getServicePriceUpdateId(item);
  const nestedType = item?.serviceType;
  const serviceType =
    item?.serviceName ||
    (typeof nestedType === "string"
      ? nestedType
      : nestedType?.serviceName ||
        nestedType?.serviceType ||
        nestedType?.title ||
        "") ||
    item?.title ||
    "";
  return {
    id,
    _id: id,
    servicePriceId,
    serviceType,
    serviceDuration:
      item?.serviceDuration ||
      item?.serviceTime ||
      nestedType?.serviceTime ||
      nestedType?.serviceDuration ||
      "",
    price: item?.price ?? "",
    createdOn: item?.createdOn || item?.createdAt || "",
    createdAt: item?.createdAt || item?.createdOn || "",
  };
};

/** Builds a dropdown option from car / sofa / tank / carpet type rows. */
export const mapCategoryTypeItem = (item: any, category: number) => {
  const id = item?._id || item?.id || "";
  let label = "";
  if (category === 0) {
    label = item?.carType || "";
  } else if (category === 2) {
    // GET /sofa/get-sofa-type returns `{ _id, type, seat }`
    label = item?.type || "";
  } else if (category === 3) {
    label = [item?.capacity, item?.unit].filter(Boolean).join(" ");
  } else if (category === 4) {
    // GET /carpet/get-carpet-type returns `{ _id, type, size }`
    label = item?.type || "";
  }
  return {
    id,
    _id: id,
    label,
    type: item?.type || "",
    carType: item?.carType || "",
    seat: item?.seat,
    capacity: item?.capacity,
    size: item?.size,
    unit: item?.unit || "",
    createdAt: item?.createdAt || "",
  };
};

const DEFAULT_SERVICE_IMAGE_KEY: { [key: number]: string } = {
  0: "services/car-foam-wash.png",
  1: "services/bike-premium-wash.png",
  2: "services/sofa-cleaning.png",
  3: "services/tank-cleaning.png",
  4: "services/carpet-cleaning.png",
};

const withOptionalId = <T extends Record<string, any>>(
  item: T,
  id?: string,
) => {
  const trimmedId = `${id || ""}`.trim();
  return trimmedId ? { ...item, _id: trimmedId } : item;
};

const toMediaItemList = (value: any) =>
  (Array.isArray(value) ? value : [])
    .map((item: any) => {
      const title = `${item?.title || item?.name || ""}`.trim();
      const image = toStoredFileUrl(
        `${item?.image || item?.imageUrl || item?.imageKey || item?.url || ""}`.trim(),
      );
      // Joi rejects empty strings ("is not allowed to be empty"); omit image when skipped
      const mapped: { title: string; image?: string } = { title };
      if (image && !isLocalPreviewUrl(image)) mapped.image = image;
      return withOptionalId(mapped, item?._id || item?.id);
    })
    .filter((item: any) => item.title);

const toTitleDescriptionList = (value: any) =>
  (Array.isArray(value) ? value : [])
    .map((item: any) => {
      const title = `${item?.title || item?.name || ""}`.trim();
      const description = `${item?.description || item?.desc || ""}`.trim();
      if (!title) {
        return null;
      }
      const mapped: { title: string; description?: string } = { title };
      if (description) mapped.description = description;
      return withOptionalId(mapped, item?._id || item?.id);
    })
    .filter(Boolean);

const mapMediaItem = (item: any) => {
  if (!item || typeof item !== "object") {
    return { title: "", image: "" };
  }
  const rawImage = `${
    item.image || item.imageUrl || item.imageKey || item.url || ""
  }`.trim();
  return withOptionalId(
    {
      title: `${item.title || item.name || ""}`.trim(),
      // Keep a displayable URL; strip only local/blob previews for storage later.
      image: isLocalPreviewUrl(rawImage) ? "" : rawImage,
    },
    item._id || item.id,
  );
};

const mapTitleDescriptionItem = (item: any) => {
  if (!item || typeof item !== "object") {
    return { title: "", description: "" };
  }
  return withOptionalId(
    {
      title: `${item.title || item.name || ""}`.trim(),
      description: `${item.description || item.desc || ""}`.trim(),
    },
    item._id || item.id,
  );
};

const unwrapServiceDetailPayload = (data: any) => {
  if (!data || typeof data !== "object") return null;
  const nested = data.data;
  const looksLikeService = (value: any) =>
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value.serviceName != null ||
      value.title != null ||
      value._id != null ||
      value.howItWorks != null ||
      value.tools != null ||
      value.benefits != null);
  // Accept either the unwrapped service object or a full API envelope.
  if (!looksLikeService(data) && looksLikeService(nested)) return nested;
  return data;
};

/** Maps GET /service-detail so nested `_id`s survive edit/update. */
export const mapServiceDetail = (data: any) => {
  const source = unwrapServiceDetailPayload(data);
  if (!source) return null;

  const serviceName = `${
    source.serviceName ?? source.title ?? source.serviceType ?? ""
  }`.trim();
  const rawImage = `${
    source.image ?? source.imageKey ?? source.serviceImage ?? ""
  }`.trim();

  return {
    _id: `${source._id ?? source.id ?? ""}`.trim(),
    category: toServiceCategory(source.category),
    typeId: `${source.typeId ?? ""}`.trim(),
    serviceName,
    serviceTime: `${source.serviceTime ?? ""}`.trim(),
    image: toStoredFileUrl(rawImage),
    /** Original image URL from API — use for cover preview. */
    imagePreviewSource: isLocalPreviewUrl(rawImage) ? "" : rawImage,
    commonQuestion: Array.isArray(source.commonQuestion)
      ? source.commonQuestion
      : [],
    howItWorks: Array.isArray(source.howItWorks)
      ? source.howItWorks.map(mapTitleDescriptionItem)
      : [],
    benefits: Array.isArray(source.benefits)
      ? source.benefits.map(mapTitleDescriptionItem)
      : [],
    tools: Array.isArray(source.tools) ? source.tools.map(mapMediaItem) : [],
    requirementFromUser: Array.isArray(source.requirementFromUser)
      ? source.requirementFromUser.map(mapMediaItem)
      : [],
  };
};

/**
 * POST /api/v1/shinology/services/create-services
 * Shared body: category, serviceName, serviceTime, image, howItWorks,
 * requirementFromUser, tools, benefits.
 */
export const buildServicePayload = (values: any) => {
  const category = Number(values?.category) || 0;
  const serviceName = `${values?.serviceName || values?.title || ""}`.trim();

  const payload: any = {
    category,
    serviceName,
    serviceTime: `${values?.serviceTime || ""}`.trim(),
    image:
      toStoredFileUrl(values?.image || values?.imageKey || "") ||
      DEFAULT_SERVICE_IMAGE_KEY[category] ||
      "",
    howItWorks: toTitleDescriptionList(values?.howItWorks),
    requirementFromUser: toMediaItemList(values?.requirementFromUser),
    tools: toMediaItemList(values?.tools),
    benefits: toTitleDescriptionList(values?.benefits),
  };

  return payload;
};

const isSameValue = (left: any, right: any) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

/**
 * PATCH /update-services/{id} — scalar keys that changed, plus the full
 * nested lists (same shape as create-services) so extra tools/requirements
 * are not dropped by a $set of a sparse diff.
 */
export const buildServiceUpdatePayload = (
  initialValues: any,
  nextValues: any,
) => {
  const initialPayload = buildServicePayload(initialValues);
  const nextPayload = buildServicePayload(nextValues);
  const patchPayload: Record<string, any> = {};

  (["category", "serviceName", "serviceTime"] as const).forEach((key) => {
    if (!isSameValue(initialPayload[key], nextPayload[key])) {
      patchPayload[key] = nextPayload[key];
    }
  });

  const initialImage = toStoredFileUrl(initialValues?.image || "");
  const nextImage = toStoredFileUrl(
    nextValues?.image || nextValues?.imageKey || "",
  );
  if (!isSameValue(initialImage, nextImage)) {
    patchPayload.image = nextImage;
  }

  (["howItWorks", "benefits", "tools", "requirementFromUser"] as const).forEach(
    (key) => {
      if (!isSameValue(initialPayload[key], nextPayload[key])) {
        patchPayload[key] = nextPayload[key];
      }
    },
  );

  if (!isSameValue(initialValues?.typeId, nextValues?.typeId)) {
    patchPayload.typeId = nextValues?.typeId || "";
  }

  const initialCommonQuestion = Array.isArray(initialValues?.commonQuestion)
    ? initialValues.commonQuestion
    : [];
  const nextCommonQuestion = Array.isArray(nextValues?.commonQuestion)
    ? nextValues.commonQuestion
    : [];
  if (!isSameValue(initialCommonQuestion, nextCommonQuestion)) {
    patchPayload.commonQuestion = nextCommonQuestion;
  }

  return patchPayload;
};

export const lookupCarTypeName = (
  carTypeId: any,
  carTypes: any[] = [],
): string => {
  const nestedName =
    typeof carTypeId === "object"
      ? carTypeId?.carType || carTypeId?.name || ""
      : "";
  const id =
    typeof carTypeId === "object"
      ? `${carTypeId?._id || carTypeId?.id || ""}`.trim()
      : `${carTypeId || ""}`.trim();
  if (!id || !Array.isArray(carTypes) || !carTypes.length) return nestedName;
  const match = carTypes.find((item: any) => {
    const typeId = `${item?._id || item?.id || ""}`.trim();
    return Boolean(typeId) && typeId === id;
  });
  return match?.carType || nestedName || "";
};

export const mapTestimonialItem = (item: any = {}) => {
  const id = `${item?._id ?? item?.id ?? ""}`.trim();
  const media = `${item?.media ?? ""}`.trim();
  const thumbnail = `${item?.thumbnail ?? ""}`.trim();
  return {
    _id: id,
    id,
    category: toServiceCategory(item?.category),
    media,
    thumbnail,
    mediaPreview: media,
    thumbnailPreview: thumbnail,
    createdAt: item?.createdAt || "",
    updatedAt: item?.updatedAt || "",
  };
};

export const mapTestimonialListResponse = (payload: any) => {
  const rows = extractPaginatedRows(payload);
  const meta = getPaginationMeta(payload);
  const result = rows.map(mapTestimonialItem);
  const page = toPositiveNumber(meta?.page, 1);
  const limit = toPositiveNumber(meta?.limit, 6);
  const total = toPositiveNumber(meta?.total, result.length);
  const totalPages = toPositiveNumber(
    meta?.totalPages,
    Math.ceil(total / limit) || 1,
  );
  return {
    result,
    page,
    limit,
    total,
    totalPages,
    nextHit: Boolean(meta?.nextHit),
    pagination: {
      page,
      limit,
      totalPages,
      totalRecords: total,
    },
  };
};

/** POST/PATCH body: category as string, thumbnail + media as stored S3 URLs. */
export const buildTestimonialPayload = (values: {
  category: number | string;
  thumbnail: string;
  media: string;
}) => ({
  category: String(toServiceCategory(values.category)),
  thumbnail: toStoredFileUrl(values.thumbnail),
  media: toStoredFileUrl(values.media),
});

export const mapVehicleListItem = (vehicle: any) => {
  const vehicleId = vehicle?.vehicleId ?? "";
  const vehicleName = vehicle?.vehicleName ?? "";
  const brandId = vehicle?.brandId ?? "";
  const brandName = vehicle?.brandName ?? "";
  const logo = vehicle?.logo ?? "";
  const vehicleType = vehicle?.vehicleType ?? 0;
  const carTypeName = vehicle?.carTypeName ?? "";

  return {
    // Keep ALL original API data
    ...vehicle,

    // Normalized/common fields used by frontend
    id: vehicleId,
    _id: vehicleId,

    vehicleId,
    vehicleName,
    name: vehicleName,

    logo,

    vehicleType,
    vehicleTypeLabel: vehicleType === 1 ? "Bike" : "Car",

    brandId,
    brandName,

    // Backward compatibility with existing frontend
    companyId: brandId,
    companyName: brandName,
    vehicleBrandId: brandId,

    carTypeId: "",
    carType: carTypeName,
    carTypeName,
    carTypeLabel: carTypeName,

    // Normalized brand/company object
    carCompany: {
      id: brandId,
      _id: brandId,
      name: brandName,
      logo,
    },
  };
};

const buildBaseUserPayload = (values: any) => {
  const payload: any = {
    firstName: values?.firstName || "",
    lastName: values?.lastName || "",
    email: values?.email || "",
    phone: values?.phoneNumber || values?.phone || "",
    line1: values?.addressLine1 || "",
    city: values?.city || "",
    state: values?.state || "",
    country: values?.country || "India",
    pincode: values?.pincode || "",
  };

  const line2 = `${values?.addressLine2 || ""}`.trim();
  if (line2) payload.line2 = line2;

  // HOME is 0 — only send after an explicit Home / Work / Other selection
  const addressType = parseSelectedAddressType(values?.addressType);
  if (addressType !== undefined) payload.addressType = addressType;

  // profilePhoto is optional — omit local preview/empty; strip signed-url query before send
  const profilePhoto = toStoredFileUrl(values?.profilePhoto || "");
  if (profilePhoto) {
    payload.profilePhoto = profilePhoto;
  }

  const [longitude, latitude] = values?.coordinates || [];
  if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
    payload.location = { type: "Point", coordinates: [longitude, latitude] };
  }

  return payload;
};

/**
 * /api/v1/admin/create-user and /api/v1/admin/update-user/{id} need the shared
 * user fields (firstName, phone, …) plus any extras for that role.
 */
export const buildUserPayloadByRole = (role: string, values: any) => {
  const payload = buildBaseUserPayload(values);

  if (role === USER_ROLE.MERCHANT) {
    payload.franchiseAmount = Number(values?.franchiseAmount) || 0;
    payload.numberOfStaff = Number(values?.numberOfStaff) || 0;
    if (values?.gstNumber) payload.gstNumber = values.gstNumber;
    if (values?.registrationNumber) {
      payload.registrationNumber = values.registrationNumber;
    }
    const dateOfJoining = toDateInputValue(values?.dateOfJoining);
    if (dateOfJoining) payload.dateOfJoining = dateOfJoining;
  }

  if (role === USER_ROLE.SERVICE_PARTNER) {
    payload.salary = Number(values?.salary) || 0;
    payload.dateOfJoining = toDateInputValue(values?.dateOfJoining);
    // create-user looks up the franchise as merchantId; vendorId is the form field
    const merchantId = values?.vendorId || values?.merchantId || "";
    if (merchantId) {
      payload.merchantId = merchantId;
    }
    const documentUrl = toStoredFileUrl(values?.document || "");
    if (documentUrl) payload.document = documentUrl;
  }

  if (role === USER_ROLE.COORDINATOR) {
    const documentUrl = toStoredFileUrl(values?.document || "");
    if (documentUrl) payload.document = documentUrl;
    if (values.salary) payload.salary = values.salary;
    if (values.dateOfJoining)
      payload.dateOfJoining = toDateInputValue(values?.dateOfJoining);
  }

  return payload;
};

/**
 * Maps GET /api/v1/admin/order/detail/{orderId} `data` into Create/Edit Order form shape.
 * Exact API keys:
 * - customerDetails: userId, name, contact, email, addressLine1/2, city, state, pincode, addressId?
 * - couponDetails: preferred coupon object (couponCode, couponValue, isPercentage, title, …)
 * - paymentDetails: paymentMode, discountMeta (legacy / fallback coupon fields)
 * - serviceDetails: serviceId, serviceName, categoryLabel, price, scheduleDate, scheduleTime
 * - merchantDetails: merchantId, name (franchise)
 * - vehicleDetails (optional): userVehicleId, vehicleName, vehicleRegistrationNumber
 */
export const mapOrderDetailForCreateForm = (data: any) => {
  const customer = data?.customerDetails || {};
  const payment = data?.paymentDetails || {};
  const service = data?.serviceDetails || {};
  const merchant = data?.merchantDetails || {};
  const vehicle = data?.vehicleDetails || {};
  const typeDetails = service?.typeDetails || {};
  const discountMeta = payment?.discountMeta || {};
  const couponMeta = resolveOrderCouponMeta(data);

  const { firstName, lastName } = splitFullName(customer?.name || "");
  // API returns `name`; keep `merchantName` fallback for older payloads
  const merchantName =
    merchant?.name || merchant?.merchantName || data?.merchantName || "";
  const { firstName: merchantFirstName, lastName: merchantLastName } =
    splitFullName(merchantName);

  const userId = customer?.userId || "";
  const rawMerchantId = merchant?.merchantId ?? data?.merchantId ?? null;
  const serviceId = service?.serviceId || "";
  const serviceName = service?.serviceName || "";
  const servicePrice = Number(service?.price) || 0;
  const serviceCategory = service?.categoryLabel || "";
  const addressId = customer?.addressId || "";
  const addressType =
    customer?.addressType !== undefined && customer?.addressType !== null
      ? `${customer.addressType}`
      : "";

  const userVehicleId =
    vehicle?.userVehicleId || typeDetails?.userVehicleId || "";
  const vehicleName =
    vehicle?.vehicleName || typeDetails?.vehicleName || "";
  const vehicleBrand =
    vehicle?.vehicleBrand || typeDetails?.vehicleBrand || "";
  const vehicleCarType =
    vehicle?.carTypeName ||
    vehicle?.carType ||
    typeDetails?.carType ||
    "";
  const vehicleRegNo =
    vehicle?.vehicleRegistrationNumber ||
    typeDetails?.registrationNumber ||
    "";
  const vehicleDisplayName =
    [vehicleBrand, vehicleName].filter(Boolean).join(" ") ||
    vehicleName ||
    vehicleRegNo ||
    "Selected vehicle";
  // Order-detail API often omits vehicleDetails/userVehicleId and only
  // returns typeDetails (name, brand, reg no) for Car/Bike services.
  const hasVehicleInfo = Boolean(
    userVehicleId || vehicleName || vehicleBrand || vehicleRegNo
  );
  const resolvedVehicleId =
    userVehicleId ||
    (hasVehicleInfo
      ? `order-vehicle-${vehicleRegNo || vehicleName || "unknown"}`
      : "");

  const scheduleDate = service?.scheduleDate || "";
  const scheduleTime = service?.scheduleTime || "";
  const scheduleMoment = parseScheduleDateTime(scheduleDate, scheduleTime);
  const serviceTimeHHmm = parseScheduleTimeToHHmm(scheduleTime);
  const hasAssignedMerchant =
    rawMerchantId !== null &&
    rawMerchantId !== undefined &&
    `${rawMerchantId}`.trim() !== "";
  // Editable only when the service is scheduled on a later calendar day
  // (tomorrow or after) — not a strict 24-hour duration check.
  const isScheduleOnNextDayOrLater = Boolean(
    scheduleMoment?.isValid() &&
      scheduleMoment
        .clone()
        .startOf("day")
        .isAfter(moment().startOf("day"))
  );
  const orderStatusRaw =
    data?.orderStatus !== undefined && data?.orderStatus !== null
      ? data.orderStatus
      : customer?.orderStatus;
  const hasOrderStatus =
    orderStatusRaw !== undefined &&
    orderStatusRaw !== null &&
    `${orderStatusRaw}` !== "";
  const orderStatusCode = hasOrderStatus ? Number(orderStatusRaw) : NaN;
  // Status 7 (cancelled by service partner): entire edit form is read-only.
  const isFullyLocked =
    orderStatusCode === ORDER_STATUS.CANCELLED_BY_SERVICE_PARTNER;
  // Franchise reassignment is allowed only when the order is still placed (0)
  // or rejected by franchise (2), and the service is on a later calendar day.
  const canReassignMerchantByStatus =
    orderStatusCode === ORDER_STATUS.ORDER_PLACED ||
    orderStatusCode === ORDER_STATUS.REJECTED_BY_MERCHANT;
  const canEditMerchant =
    !isFullyLocked &&
    canReassignMerchantByStatus &&
    isScheduleOnNextDayOrLater;
  // Rejected-by-franchise: show empty franchise so admin must pick again.
  const franchiseId =
    orderStatusCode === ORDER_STATUS.REJECTED_BY_MERCHANT
      ? ""
      : rawMerchantId || "";

  const selectedCustomer = userId
    ? {
        id: userId,
        _id: userId,
        fullName: customer?.name || "",
        firstName,
        lastName,
        phoneNumber: customer?.contact || "",
        phone: customer?.contact || "",
        email: customer?.email || "",
      }
    : null;

  const selectedFranchise = franchiseId
    ? {
        id: franchiseId,
        _id: franchiseId,
        fullName: merchantName,
        firstName: merchantFirstName,
        lastName: merchantLastName,
        phoneNumber: merchant?.contact || "",
        phone: merchant?.contact || "",
        email: merchant?.email || "",
      }
    : null;

  const serviceOption = serviceId
    ? {
        _id: serviceId,
        serviceName: serviceName || "Selected service",
        price: servicePrice,
        serviceTime: service?.serviceTime || "",
        image: service?.image || "",
      }
    : null;

  const vehicleOption = hasVehicleInfo
    ? {
        id: resolvedVehicleId,
        _id: resolvedVehicleId,
        userVehicleId: resolvedVehicleId,
        name: vehicleDisplayName,
        vehicleName,
        vehicleBrand,
        carType: vehicleCarType,
        registrationNumber: vehicleRegNo,
        vehicleRegistrationNumber: vehicleRegNo,
      }
    : null;

  const addressOption = addressId
    ? {
        _id: addressId,
        firstName: customer?.name || "",
        addressLine1: customer?.addressLine1 || "",
        addressLine2: customer?.addressLine2 || "",
        city: customer?.city || "",
        state: customer?.state || "",
        pincode: customer?.pincode || "",
        isDefault: true,
        location: customer?.location,
      }
    : null;

  const [lng = "", lat = ""] = customer?.location?.coordinates || [];
  // discountMeta.isPercentage is the source of truth:
  // false → flat discount (discountMeta.discount); true → percentage coupon.
  const hasDiscountMeta =
    discountMeta &&
    typeof discountMeta === "object" &&
    Object.keys(discountMeta).length > 0;
  const isPercentageFromMeta =
    hasDiscountMeta && discountMeta.isPercentage !== undefined
      ? Boolean(discountMeta.isPercentage)
      : null;
  const mappedCoupon = mapOrderCoupon(data);
  const isFlatDiscount =
    isPercentageFromMeta === false ||
    (!mappedCoupon &&
      (toPositiveAmount(discountMeta?.discount) > 0 ||
        toPositiveAmount(payment?.discount) > 0));
  const isPercentageDiscount =
    !isFlatDiscount &&
    (isPercentageFromMeta === true || Boolean(mappedCoupon?.isPercentage));
  const couponOption = isFlatDiscount ? null : mappedCoupon;
  const flatDiscount = isFlatDiscount
    ? toPositiveAmount(discountMeta?.discount) ||
      toPositiveAmount(payment?.discount) ||
      resolveOrderFlatDiscount(data) ||
      0
    : 0;
  // Coupon (percentage) and flat discount are mutually exclusive.
  const discountValue = isFlatDiscount
    ? flatDiscount || ""
    : couponOption && !couponOption.isPercentage
      ? toPositiveAmount(couponOption.couponValue) || ""
      : "";

  return {
    formValues: {
      userId,
      franchiseId,
      paymentMode:
        payment?.paymentMode !== undefined && payment?.paymentMode !== null
          ? `${payment.paymentMode}`
          : "",
      serviceCategory,
      serviceId,
      vechileId: resolvedVehicleId,
      vehicleRegNo,
      price: servicePrice,
      discountValue,
      // Flat discount must never carry a fake couponCode like "undefined".
      couponCode: isFlatDiscount ? "" : couponOption?.couponCode || "",
      couponId: isFlatDiscount
        ? ""
        : couponOption?._id || couponOption?.id || "",
      isPercentage: isPercentageDiscount,
      isFlatDiscount,
      serviceDate: scheduleDate
        ? moment(scheduleDate).format("YYYY-MM-DD")
        : "",
      serviceTime: serviceTimeHHmm,
      addressId,
      addressType,
      addressLine1: customer?.addressLine1 || "",
      addressLine2: customer?.addressLine2 || "",
      city: customer?.city || "",
      state: customer?.state || "",
      pincode: customer?.pincode || "",
      longitude: lng != null ? String(lng) : "",
      latitude: lat != null ? String(lat) : "",
    },
    selectedCustomer,
    selectedFranchise,
    serviceOption,
    vehicleOption,
    addressOption,
    couponOption,
    isFlatDiscount,
    isPercentageDiscount,
    editPermissions: {
      hasAssignedMerchant,
      isScheduleOnNextDayOrLater,
      /** @deprecated Use isScheduleOnNextDayOrLater */
      isScheduleBeyond24Hours: isScheduleOnNextDayOrLater,
      isFullyLocked,
      // Franchise dropdown is editable only when orderStatus is 0 or 2
      // and the service is on a later calendar day.
      canEditMerchant,
      // Address / schedule / vehicle reg only when a merchant is already assigned
      // and the order is not fully locked (status 7).
      canEditOrderDetails:
        !isFullyLocked &&
        isScheduleOnNextDayOrLater &&
        hasAssignedMerchant,
    },
    raw: {
      customerDetails: customer,
      paymentDetails: payment,
      serviceDetails: service,
      merchantDetails: merchant,
      vehicleDetails: vehicle,
    },
  };
};

const cleanMetaText = (value: any) => {
  const text = `${value ?? ""}`.trim();
  if (!text || text.toLowerCase() === "undefined" || text.toLowerCase() === "null") {
    return "";
  }
  return text;
};

const toPositiveAmount = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Flat rupee discount from `paymentDetails.discountMeta`.
 * When `discountMeta.isPercentage === false`, use `discountMeta.discount`
 * (mutually exclusive with percentage coupon). Prefer that over couponCode
 * placeholders like `"undefined"`.
 */
export const resolveOrderFlatDiscount = (data: any) => {
  const payment = data?.paymentDetails || {};
  const discountMeta = payment?.discountMeta || {};

  // Explicit flat discount from API — ignore fake couponCode values.
  if (discountMeta?.isPercentage === false) {
    return (
      toPositiveAmount(discountMeta?.discount) ||
      toPositiveAmount(payment?.discount) ||
      toPositiveAmount(discountMeta?.value) ||
      0
    );
  }

  // Percentage coupon — not a flat discount.
  if (discountMeta?.isPercentage === true) return 0;

  // No explicit flag: only treat as flat when there is no real coupon.
  if (mapOrderCoupon(data)) return 0;

  return (
    toPositiveAmount(discountMeta?.discount) ||
    toPositiveAmount(payment?.discount) ||
    toPositiveAmount(discountMeta?.value) ||
    0
  );
};

/** Flat rupee discount for the create/edit form. */
const readFlatDiscountAmount = (payment: any = {}, discountMeta: any = {}) => {
  if (discountMeta?.isPercentage) return "";
  const fromMeta = toPositiveAmount(discountMeta?.discount);
  if (fromMeta) return fromMeta;
  const fromPayment = toPositiveAmount(payment?.discount);
  if (fromPayment) return fromPayment;
  const fromValue = toPositiveAmount(discountMeta?.value);
  if (fromValue) return fromValue;
  return "";
};

const normalizeCouponSource = (raw: any) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    return (
      raw.find(
        (item) =>
          cleanMetaText(item?.couponCode || item?.code) || item?._id || item?.id,
      ) || null
    );
  }
  if (typeof raw === "string") {
    const code = cleanMetaText(raw);
    return code ? { couponCode: code } : null;
  }
  if (typeof raw !== "object") return null;
  // Empty / placeholder objects (e.g. {}) are not usable coupon sources.
  if (
    !cleanMetaText(raw.couponCode || raw.code) &&
    !raw._id &&
    !raw.id
  ) {
    return null;
  }
  return raw;
};

const findCouponLike = (value: any, depth = 0): any => {
  if (!value || depth > 5) return null;
  if (typeof value === "string") {
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findCouponLike(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof value !== "object") return null;
  if (cleanMetaText(value.couponCode)) return value;
  for (const nested of Object.values(value)) {
    if (nested && typeof nested === "object") {
      const found = findCouponLike(nested, depth + 1);
      if (found) return found;
    }
  }
  return null;
};

const pickOrderCouponSource = (data: any) => {
  // Prefer top-level couponDetails (current API). discountMeta is legacy / incomplete.
  const candidates = [
    data?.couponDetails,
    data?.coupon,
    data?.appliedCoupon,
    data?.coupons,
    data?.paymentDetails?.couponDetails,
    data?.paymentDetails?.coupon,
    data?.paymentDetails?.appliedCoupon,
    data?.serviceDetails?.coupon,
  ];
  for (const candidate of candidates) {
    const fromDirect = normalizeCouponSource(candidate);
    if (fromDirect) return fromDirect;
  }
  const code = cleanMetaText(
    data?.couponCode ||
      data?.paymentDetails?.couponCode ||
      data?.paymentDetails?.discountMeta?.couponCode,
  );
  if (code) {
    const discountMeta = data?.paymentDetails?.discountMeta || {};
    return {
      couponCode: code,
      couponValue: discountMeta?.couponValue ?? discountMeta?.value,
      isPercentage: discountMeta?.isPercentage,
      title: discountMeta?.title,
      description: discountMeta?.description,
      image: discountMeta?.image,
    };
  }
  return findCouponLike(data);
};

/** Coupon on the edit-order form is mapped from the order-detail payload only. */
export const mapOrderCoupon = (data: any) => {
  const source = pickOrderCouponSource(data);
  if (!source) return null;
  const mapped = mapCouponItem(source);
  const couponCode = cleanMetaText(mapped?.couponCode);
  if (!couponCode) return null;
  return { ...mapped, couponCode };
};

/**
 * Unified coupon fields for order detail + edit form.
 * Prefers `couponDetails`; falls back to a valid `discountMeta` (ignores couponCode "undefined").
 */
export const resolveOrderCouponMeta = (data: any) => {
  const discountMeta = data?.paymentDetails?.discountMeta || {};
  // isPercentage: false → flat discount only; do not treat as coupon.
  if (discountMeta?.isPercentage === false) {
    return null;
  }

  const coupon = mapOrderCoupon(data);
  if (coupon) {
    return {
      _id: coupon._id || coupon.id || "",
      couponCode: coupon.couponCode,
      couponValue: Number(coupon.couponValue) || 0,
      isPercentage: Boolean(coupon.isPercentage),
      title: cleanMetaText(coupon.title),
      description: cleanMetaText(coupon.description),
      image: cleanMetaText(coupon.imagePreview || coupon.image),
    };
  }

  const code = cleanMetaText(discountMeta?.couponCode);
  const couponValue = Number(discountMeta?.couponValue);
  const legacyValue = Number(discountMeta?.value);
  const enteredDiscount =
    Number.isFinite(couponValue) && couponValue > 0
      ? couponValue
      : Number.isFinite(legacyValue) && legacyValue > 0
        ? legacyValue
        : 0;
  const title = cleanMetaText(discountMeta?.title);
  const description = cleanMetaText(discountMeta?.description);
  const image = cleanMetaText(discountMeta?.image);

  if (!code && enteredDiscount <= 0 && !title && !description && !image) {
    return null;
  }

  return {
    _id: "",
    couponCode: code,
    couponValue: enteredDiscount,
    isPercentage: Boolean(discountMeta?.isPercentage),
    title,
    description,
    image,
  };
};

/**
 * View model for Order Detail page from the same order-detail API payload.
 */
export const mapOrderDetailView = (data: any) => {
  const customer = data?.customerDetails || {};
  const payment = data?.paymentDetails || {};
  const service = data?.serviceDetails || {};
  const merchant = data?.merchantDetails || {};
  const vehicle = data?.vehicleDetails || {};
  const couponMeta = resolveOrderCouponMeta(data);
  const flatDiscount = resolveOrderFlatDiscount(data);

  const servicePrice = Number(service?.price) || 0;
  const hasCoupon = Boolean(couponMeta?.couponCode);
  const isPercentage = Boolean(couponMeta?.isPercentage);
  const enteredDiscount = Number(couponMeta?.couponValue) || 0;
  // Either percentage coupon OR flat discountMeta.discount — never both.
  const discountValue = hasCoupon
    ? isPercentage
      ? Math.max(0, (servicePrice * enteredDiscount) / 100)
      : Math.max(0, enteredDiscount)
    : Math.max(0, flatDiscount);
  const discountImage = cleanMetaText(couponMeta?.image);
  const afterDiscount = Math.max(0, servicePrice - discountValue);
  const taxValue =
    payment?.tax != null
      ? Number(payment.tax)
      : Number(((afterDiscount * 18) / 100).toFixed(2));
  const finalPrice =
    payment?.finalPrice != null
      ? Number(payment.finalPrice)
      : afterDiscount + taxValue;

  const addressParts = [
    customer?.addressLine1,
    customer?.addressLine2,
    customer?.city,
    customer?.state,
    customer?.pincode,
  ].filter(Boolean);

  const scheduleDate = service?.scheduleDate || "";
  const scheduleTime = service?.scheduleTime || "";
  let scheduleLabel = "N/A";
  if (scheduleDate || scheduleTime) {
    const datePart = scheduleDate
      ? moment(scheduleDate).format("DD MMMM YYYY")
      : "";
    const parsedHHmm = parseScheduleTimeToHHmm(scheduleTime);
    const timePart = parsedHHmm
      ? moment(parsedHHmm, "HH:mm").format("hh:mm A")
      : "";
    scheduleLabel = [timePart, datePart].filter(Boolean).join(", ");
  }

  const typeDetails = service?.typeDetails || {};
  const typeDetailsLabel = Object.entries(typeDetails)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ");

  return {
    orderId: data?.orderId || data?._id || "",
    orderStatus:
      data?.orderStatus !== undefined && data?.orderStatus !== null
        ? `${data.orderStatus}`
        : customer?.orderStatus !== undefined && customer?.orderStatus !== null
          ? `${customer.orderStatus}`
          : "",
    reason: data?.reason || data?.cancellationReason || "",
    scheduleLabel,
    scheduleDate,
    scheduleTime,
    customer: {
      userId: customer?.userId || "",
      name: customer?.name || "N/A",
      contact: customer?.contact || "",
      email: customer?.email || "",
      address: customer?.address || addressParts.join(", "),
      addressLine1: customer?.addressLine1 || "",
      addressLine2: customer?.addressLine2 || "",
      city: customer?.city || "",
      state: customer?.state || "",
      pincode: customer?.pincode || "",
    },
    merchant: {
      merchantId: merchant?.merchantId || data?.merchantId || "",
      merchantName:
        merchant?.name || merchant?.merchantName || data?.merchantName || "",
      contact: merchant?.contact || "",
      email: merchant?.email || "",
    },
    service: {
      serviceId: service?.serviceId || "",
      serviceName: service?.serviceName || "N/A",
      category: service?.category,
      categoryLabel: service?.categoryLabel || "",
      serviceTime: service?.serviceTime || "",
      price: servicePrice,
      image: service?.image || "",
      typeDetails,
      typeDetailsLabel,
    },
    vehicle: {
      userVehicleId: vehicle?.userVehicleId || typeDetails?.userVehicleId || "",
      vehicleName:
        vehicle?.vehicleName || typeDetails?.vehicleName || "",
      vehicleBrand:
        vehicle?.vehicleBrand || typeDetails?.vehicleBrand || "",
      vehicleRegistrationNumber:
        vehicle?.vehicleRegistrationNumber ||
        typeDetails?.registrationNumber ||
        "",
      carTypeName:
        vehicle?.carTypeName ||
        vehicle?.carType ||
        typeDetails?.carType ||
        "",
    },
    payment: {
      paymentId: payment?.paymentId || "",
      paymentMode: payment?.paymentMode,
      paymentStatus: payment?.paymentStatus,
      discountValue,
      hasCoupon,
      couponCode: cleanMetaText(couponMeta?.couponCode),
      couponValue: enteredDiscount,
      isPercentage,
      title: cleanMetaText(couponMeta?.title),
      description: cleanMetaText(couponMeta?.description),
      image: discountImage,
      servicePrice,
      tax: taxValue,
      currency: `${payment?.currency || "INR"}`.trim() || "INR",
      reportedFinalPrice:
        payment?.finalPrice != null && `${payment.finalPrice}` !== ""
          ? Number(payment.finalPrice)
          : null,
      finalPrice,
    },
    coupon: mapOrderCoupon(data),
    // API may return `feedbackDetails: null` when no feedback exists.
    feedbackDetails: mapFeedbackRow(data?.feedbackDetails),
    orderFeedbacks: Array.isArray(data?.orderFeedbacks)
      ? data.orderFeedbacks
      : [],
  };
};

const toOptionalNumber = (value: any) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const ALL_CATEGORY_VALUES = ALL_COUPON_CATEGORIES.map((category) =>
  Number(category)
);

const toCouponCategories = (item: any = {}): number[] => {
  const raw = Array.isArray(item?.category)
    ? item.category
    : Array.isArray(item?.categories)
      ? item.categories
      : [];
  return Array.from(
    new Set(raw.map((category: any) => toServiceCategory(category)))
  );
};

const isGlobalCouponCategories = (categories: number[] = []) =>
  ALL_CATEGORY_VALUES.every((category) => categories.includes(category)) &&
  categories.length >= ALL_CATEGORY_VALUES.length;

const toIsoDateTime = (value: any) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const mapCouponCategoriesLabel = (
  couponType?: string,
  categories: any[] = []
) => {
  if (couponType === COUPON_TYPE.GLOBAL) return "All";
  const labels = (categories || [])
    .map((item) => {
      const category = toServiceCategory(item);
      return (
        COUPON_CATEGORY_OPTIONS.find((option) => option.id === category)?.label ||
        SERVICE_CATEGORY_LABEL[category] ||
        ""
      );
    })
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "N/A";
};

export const mapCouponItem = (item: any = {}) => {
  const id = `${item?._id ?? item?.id ?? item?.couponId ?? ""}`.trim();
  const categories = toCouponCategories(item);
  const couponType = isGlobalCouponCategories(categories)
    ? COUPON_TYPE.GLOBAL
    : COUPON_TYPE.SPECIFIC_CATEGORIES;
  const isPercentage = Boolean(item?.isPercentage);
  const couponValue = Number(item?.couponValue ?? item?.percentageOff) || 0;
  const image = toStoredFileUrl(item?.image || item?.bannerImage || "");
  const imagePreview = `${item?.imagePreview || item?.bannerImage || image || ""}`.trim();
  const title = `${item?.title || item?.couponName || ""}`.trim();
  const couponCode = `${item?.couponCode || ""}`.trim();
  const startTime = item?.startTime || item?.startDate || "";
  const endTime = item?.endTime || item?.endDate || "";
  const couponLevelRedemption = toOptionalNumber(
    item?.couponLevelRedemption ?? item?.maxCouponRedemption
  );
  const userLevelRedemption = toOptionalNumber(
    item?.userLevelRedemption ?? item?.maxUserRedemption
  );
  const reedemedCouponCount = toOptionalNumber(
    item?.reedemedCouponCount ?? item?.couponRedeemedCount
  );

  return {
    _id: id,
    id,
    couponCode,
    title,
    description: `${item?.description || ""}`.trim(),
    couponName: title || couponCode,
    image,
    imagePreview,
    bannerImage: imagePreview || image,
    couponType,
    categories,
    category: categories,
    couponValue,
    isPercentage,
    percentageOff: isPercentage ? couponValue : 0,
    couponLevelRedemption,
    userLevelRedemption,
    reedemedCouponCount,
    startTime,
    endTime,
    startDate: startTime,
    endDate: endTime,
    status: [
      DISCOUNT_STATUS.ACTIVE,
      DISCOUNT_STATUS.INACTIVE,
      DISCOUNT_STATUS.EXPIRED,
      DISCOUNT_STATUS.UPCOMING,
    ].includes(Number(item?.status))
      ? Number(item?.status)
      : DISCOUNT_STATUS.INACTIVE,
    isDefault: Boolean(item?.isDefault),
    createdOn: item?.createdOn || item?.createdAt || "",
    createdAt: item?.createdAt || item?.createdOn || "",
    updatedAt: item?.updatedAt || "",
  };
};

/**
 * Request payload for createCoupon / updateCoupon.
 * Keys are listed explicitly so stale form/API fields (e.g. `maxRedemption`)
 * can never leak into the request.
 */
export const mapCreateCouponPayload = (values: any = {}) => {
  const isPercentage =
    values?.isPercentage === true || values?.isPercentage === "true";
  const selectedCategories = toCouponCategories(values);
  // No selection is treated as a global coupon (every category).
  const categories = selectedCategories.length
    ? selectedCategories
    : [...ALL_CATEGORY_VALUES];

  const payload: Record<string, any> = {
    couponValue: Number(values?.couponValue),
    couponCode: `${values?.couponCode || ""}`.trim(),
    isPercentage,
    image: toStoredFileUrl(values?.image || values?.bannerImage || ""),
    title: `${values?.title || values?.couponName || ""}`.trim(),
    description: `${values?.description || ""}`.trim(),
    category: categories,
    startTime: toIsoDateTime(values?.startTime || values?.startDate),
    endTime: toIsoDateTime(values?.endTime || values?.endDate),
  };

  // Backend requires isDefault on every create, including when unchecked.
  payload.isDefault = Boolean(values?.isDefault);

  const couponLevelRedemption = toOptionalNumber(
    values?.couponLevelRedemption
  );
  if (couponLevelRedemption !== undefined) {
    payload.couponLevelRedemption = couponLevelRedemption;
  }

  const userLevelRedemption = toOptionalNumber(values?.userLevelRedemption);
  if (userLevelRedemption !== undefined) {
    payload.userLevelRedemption = userLevelRedemption;
  }

  return payload;
};

const normalizeCouponCategories = (categories: any[] = []) =>
  [...categories]
    .map((category) => Number(category))
    .sort((left, right) => left - right);

const isSameCouponDateTime = (left: any, right: any) => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  const leftTime = moment(left).valueOf();
  const rightTime = moment(right).valueOf();
  if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) {
    return isSameValue(left, right);
  }
  return leftTime === rightTime;
};

/** PATCH /update-coupon/{id} — only keys that changed vs the loaded coupon. */
export const mapUpdateCouponPayload = (
  initialValues: any = {},
  nextValues: any = {}
) => {
  const initialPayload = mapCreateCouponPayload(initialValues);
  const nextPayload = mapCreateCouponPayload(nextValues);
  const patchPayload: Record<string, any> = {};

  (
    [
      "couponValue",
      "couponCode",
      "isPercentage",
      "image",
      "title",
      "description",
      "category",
      "endTime",
      "couponLevelRedemption",
      "userLevelRedemption",
    ] as const
  ).forEach((key) => {
    if (key === "category") {
      if (
        !isSameValue(
          normalizeCouponCategories(initialPayload.category),
          normalizeCouponCategories(nextPayload.category)
        )
      ) {
        patchPayload.category = nextPayload.category;
      }
      return;
    }

    if (key === "endTime") {
      if (!isSameCouponDateTime(initialPayload[key], nextPayload[key])) {
        patchPayload[key] = nextPayload[key];
      }
      return;
    }

    if (!isSameValue(initialPayload[key], nextPayload[key])) {
      patchPayload[key] = nextPayload[key];
    }
  });

  // Always send isDefault so an unchecked checkbox still satisfies the API.
  patchPayload.isDefault = Boolean(nextValues?.isDefault);

  return patchPayload;
};

export const mapCouponFormValues = (
  item: any = {}
): typeof INITIAL_COUPON_VALUES & { _originalStartTime?: string } => {
  const mapped = mapCouponItem(item);
  const startTime = mapped.startTime || "";
  return {
    ...INITIAL_COUPON_VALUES,
    couponCode: mapped.couponCode,
    title: mapped.title,
    description: mapped.description,
    couponValue:
      mapped.couponValue === 0 || mapped.couponValue
        ? `${mapped.couponValue}`
        : "",
    isPercentage: Boolean(mapped.isPercentage),
    image: mapped.imagePreview || mapped.image,
    categories: mapped.categories.length
      ? mapped.categories
      : [...INITIAL_COUPON_VALUES.categories],
    startTime,
    endTime: mapped.endTime || "",
    isDefault: Boolean(mapped.isDefault),
    couponLevelRedemption:
      mapped.couponLevelRedemption === 0 || mapped.couponLevelRedemption
        ? `${mapped.couponLevelRedemption}`
        : "",
    userLevelRedemption:
      mapped.userLevelRedemption === 0 || mapped.userLevelRedemption
        ? `${mapped.userLevelRedemption}`
        : "",
    // Used by Yup so edit can keep an already-started coupon's startTime.
    _originalStartTime: startTime,
  };
};

export const mapCouponDetailView = (item: any = {}) => {
  const mapped = mapCouponItem(item);
  return {
    ...mapped,
    couponTypeLabel: getCouponTypeLabel(mapped.couponType),
    statusLabel: getCouponStatusLabel(mapped.status),
    categoriesLabel: mapCouponCategoriesLabel(
      mapped.couponType,
      mapped.categories
    ),
  };
};

const orderCouponStorageKey = (orderId: string) => `orderCouponId:${orderId}`;
const orderCouponDataKey = (orderId: string) => `orderCoupon:${orderId}`;

export const persistOrderCouponId = (orderId?: string, couponId?: string) => {
  const oid = `${orderId || ""}`.trim();
  const cid = `${couponId || ""}`.trim();
  if (!oid || !cid) return;
  try {
    sessionStorage.setItem(orderCouponStorageKey(oid), cid);
    localStorage.setItem(orderCouponStorageKey(oid), cid);
  } catch {
    /* ignore quota / private mode */
  }
};

export const persistOrderCoupon = (orderId?: string, coupon?: any) => {
  const oid = `${orderId || ""}`.trim();
  if (!oid || !coupon) return;
  persistOrderCouponId(oid, coupon._id || coupon.id);
  try {
    const snapshot = JSON.stringify(coupon);
    sessionStorage.setItem(orderCouponDataKey(oid), snapshot);
    localStorage.setItem(orderCouponDataKey(oid), snapshot);
  } catch {
    /* ignore quota / private mode */
  }
};

export const readOrderCouponId = (orderId?: string): string => {
  const oid = `${orderId || ""}`.trim();
  if (!oid) return "";
  try {
    return (
      sessionStorage.getItem(orderCouponStorageKey(oid)) ||
      localStorage.getItem(orderCouponStorageKey(oid)) ||
      ""
    ).trim();
  } catch {
    return "";
  }
};

export const readOrderCoupon = (orderId?: string) => {
  const oid = `${orderId || ""}`.trim();
  if (!oid) return null;
  try {
    const raw =
      sessionStorage.getItem(orderCouponDataKey(oid)) ||
      localStorage.getItem(orderCouponDataKey(oid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const findCouponById = (rows: any[] = [], couponId?: string) => {
  const id = `${couponId || ""}`.trim();
  if (!id) return null;
  return (
    rows.find((item) => `${item?._id || item?.id || ""}`.trim() === id) || null
  );
};

/** createNewOrder coupon is a single object, not an array. */
export const normalizeOrderCouponPayload = (coupon: any) => {
  if (!coupon) return undefined;
  const source = Array.isArray(coupon)
    ? coupon.find((item) => item?.couponCode) || coupon[0]
    : coupon;
  const couponCode = `${source?.couponCode || source || ""}`.trim();
  return couponCode ? { couponCode } : undefined;
};
